import { useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput, PennMultiSelect } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { machineOptionSchema, type MachineOptionFormData } from "@/types/admin/machineOptions.types";
import {
  useMachineOptions,
  useMachineOption,
  useMachineNames,
  useCreateMachineOption,
  useUpdateMachineOption,
  useDeleteMachineOption,
} from "@/hooks/useMachineOptions";
import type { MachineOption } from "@/api/machineOptions";

const CURRENCY_SYMBOL: Record<string, string> = { EUR: "€", USD: "$" };

/** Format a decimal price string to 2dp without float (§L — never Number(price)). */
const formatPrice = (value: string | null, currency: string | null): string => {
  if (value === null) return "—";
  const [intPart, frac = ""] = value.split(".");
  const cents = `${frac}00`.slice(0, 2);
  const sym = currency ? (CURRENCY_SYMBOL[currency] ?? `${currency} `) : "";
  return `${sym}${intPart}.${cents}`;
};

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: MachineOption } | null;

const blank: MachineOptionFormData = { partNo: "", description: "", category: "", subCategory: "", price: 0, availableOn: [] };

export const MachineOptionsPage = ({
  title = "Machine Options (EU)",
  subtitle = "Dataset: Machine Options (EU)",
  regionId = 1,
}: {
  title?: string;
  subtitle?: string;
  /** 1 = EU (default), 2 = US. Scopes all list/create/update calls. */
  regionId?: number;
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const params = useMemo(
    () => ({ page: pageIndex + 1, limit: pageSize, regionId: regionId, search: search || undefined }),
    [pageIndex, pageSize, regionId, search],
  );

  const { data, isLoading, isFetching, isError, error } = useMachineOptions(params);
  const rows: MachineOption[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<MachineOption | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Load detail when opening edit/view (prefills the form with live data)
  const editingPartNo = modal?.mode !== "add" ? (modal?.item?.partNo ?? null) : null;
  const detail = useMachineOption(editingPartNo, regionId);

  // Machine names for the "Available On" multi-select (from svc-04).
  const { data: machineNames, isLoading: machineNamesLoading } = useMachineNames();
  const machineNameOptions = useMemo(
    () =>
      (machineNames ?? []).map((m) => ({
        value: m.machineName,
        label: m.productName ? `${m.machineName} — ${m.productName}` : m.machineName,
      })),
    [machineNames],
  );

  const createMutation = useCreateMachineOption();
  const updateMutation = useUpdateMachineOption();
  const deleteMutation = useDeleteMachineOption();

  const { register, control, handleSubmit, reset, formState: { errors } } = useForm<MachineOptionFormData>({ resolver: zodResolver(machineOptionSchema) });

  const openAdd = () => {
    setFormError(null);
    reset(blank);
    setModal({ mode: "add" });
  };

  const openEdit = (item: MachineOption) => {
    setFormError(null);
    reset({
      partNo: item.partNo,
      description: item.description ?? "",
      category: item.category ?? "",
      subCategory: item.subCategory ?? "",
      price: item.price ? Number(item.price) : 0,
      availableOn: item.availableOn,
    });
    setModal({ mode: "edit", item });
  };

  const openView = (item: MachineOption) => {
    setFormError(null);
    reset({
      partNo: item.partNo,
      description: item.description ?? "",
      category: item.category ?? "",
      subCategory: item.subCategory ?? "",
      price: item.price ? Number(item.price) : 0,
      availableOn: item.availableOn,
    });
    setModal({ mode: "view", item });
  };

  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (form: MachineOptionFormData) => {
    setFormError(null);
    const common = {
      description: form.description?.trim() || null,
      category: form.category?.trim() || null,
      subCategory: form.subCategory?.trim() || null,
      price: String(form.price),
      availableOn: form.availableOn,
      regionId: regionId,
    };

    if (modal?.mode === "edit" && modal.item) {
      updateMutation.mutate(
        { partNo: modal.item.partNo, body: common },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save machine option.")),
        },
      );
    } else {
      createMutation.mutate(
        { partNo: form.partNo.trim(), ...common },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to create machine option.")),
        },
      );
    }
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<MachineOption>[] = [
    { header: "Part No", key: "partNo" },
    { header: "Description", render: (i) => i.description ?? "—" },
    { header: "Category", render: (i) => i.category ?? "—" },
    { header: "Sub-category", render: (i) => i.subCategory ?? "—" },
    { header: "Price", render: (i) => formatPrice(i.price, i.currencyCode) },
    { header: "Available On", render: (i) => <span className="text-blue_grey3 truncate max-w-xs block text-small">{i.availableOn.join(", ") || "—"}</span> },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = { add: `Add ${title} record`, edit: `Edit ${title} record`, view: `View ${title} record` };

  return (
    <>
      <PageHeader title={title} subtitle={`${subtitle} · ${total} records`} action={<AddButton onClick={openAdd} />} />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load machine options{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          {deleteError}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search part no…"
      />

      <AdminTable columns={cols} rows={rows} keyExtractor={(i) => i.partNo} loading={isLoading} />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        footer={
          isView
            ? <CancelButton onClick={closeModal} />
            : <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
            {formError && (
              <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}
            <PennField label="Part No" htmlFor="partNo" required={!isView} error={!isView ? errors.partNo?.message : undefined}>
              <PennInput id="partNo" disabled={isView || modal?.mode === "edit"} error={!isView && !!errors.partNo} {...register("partNo")} />
            </PennField>
            <PennField label="Description" htmlFor="description" required={!isView} error={!isView ? errors.description?.message : undefined}>
              <PennInput id="description" disabled={isView} error={!isView && !!errors.description} {...register("description")} />
            </PennField>
            <PennField label="Category" htmlFor="category" required={!isView} error={!isView ? errors.category?.message : undefined}>
              <PennInput id="category" disabled={isView} error={!isView && !!errors.category} {...register("category")} />
            </PennField>
            <PennField label="Sub-category" htmlFor="subCategory" error={!isView ? errors.subCategory?.message : undefined}>
              <PennInput id="subCategory" disabled={isView} error={!isView && !!errors.subCategory} {...register("subCategory")} />
            </PennField>
            <PennField label="Price" htmlFor="price" required={!isView} error={!isView ? errors.price?.message : undefined}>
              <PennInput id="price" type="number" step="0.01" disabled={isView} error={!isView && !!errors.price} {...register("price")} />
            </PennField>
            <PennField className="col-span-1 sm:col-span-2" label="Available On" htmlFor="availableOn" required={!isView} error={!isView ? errors.availableOn?.message : undefined} hint={isView ? undefined : "Select one or more machines"}>
              <Controller
                name="availableOn"
                control={control}
                render={({ field }) => (
                  <PennMultiSelect
                    inputId="availableOn"
                    options={machineNameOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isView}
                    isLoading={machineNamesLoading}
                    error={!isView && !!errors.availableOn}
                    placeholder="Select machines…"
                  />
                )}
              />
            </PennField>
          </form>
        )}
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          setDeleteError(null);
          deleteMutation.mutate(
            { partNo: deleteTarget.partNo, regionId },
            {
              onError: (e) => setDeleteError(apiErrorMessage(e, "Failed to delete machine option.")),
              onSettled: () => setDeleteTarget(null),
            },
          );
        }}
        itemLabel={deleteTarget?.partNo}
      />
    </>
  );
};
